import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, BookOpen, Settings, Loader2, ChevronLeft } from 'lucide-react';
import DarkModeToggle from '@/components/DarkModeToggle';

/**
 * DrawerItem renders one row in the hamburger drawer.
 *
 * item shape:
 *   icon       — lucide component
 *   label      — string
 *   onClick    — fn (called after drawer closes)
 *   disabled   — bool
 *   spinning   — bool  (animates the icon)
 *   badge      — bool  (amber dot — e.g. unsaved changes)
 *   variant    — 'default' | 'active' | 'destructive'
 *   testId     — data-testid string
 */
function DrawerItem({ item, onClose }) {
  const Icon = item.spinning ? Loader2 : item.icon;

  const rootCls = [
    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors disabled:opacity-50',
    item.variant === 'destructive'
      ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
      : item.variant === 'active'
      ? 'bg-[#F0E7DC]/60 dark:bg-[#2A2520]/40 hover:bg-[#F0E7DC] dark:hover:bg-[#2A2520]/50'
      : 'hover:bg-[#F0E7DC] dark:hover:bg-[#2A2520]/50',
  ].join(' ');

  const iconCls = [
    'w-4 h-4 flex-shrink-0',
    item.spinning ? 'animate-spin' : '',
    item.variant === 'destructive' ? 'text-red-500' : 'text-[#5F554B] dark:text-[#C8BFB4]',
  ].join(' ');

  const labelCls = [
    'text-sm flex-1',
    item.variant === 'destructive' ? 'text-red-600 dark:text-red-400' : 'text-[#2A2420] dark:text-[#F0EBE4]',
  ].join(' ');

  return (
    <button
      onClick={() => { onClose(); item.onClick?.(); }}
      disabled={item.disabled}
      data-testid={item.testId}
      className={rootCls}
    >
      <Icon className={iconCls} />
      <span className={labelCls}>{item.label}</span>
      {item.badge && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />}
    </button>
  );
}

const DIVIDER = <div className="border-t border-[#E7DED1]/70 dark:border-[#302820]/60 my-3" />;

/**
 * AppShell — shared page wrapper for all authenticated routes.
 *
 * Provides:
 *   • Warm parchment / charcoal page background
 *   • Fixed floating hamburger button (top-left)
 *   • Slide-in drawer with contextual items (per page) + global nav
 *
 * Props:
 *   contextualItems  array of DrawerItem descriptors (see above), with
 *                    { divider: true } objects to insert separators
 *   backTo           { label: string, onClick: fn } — shows a back button next to the hamburger
 *   className        extra classes added to the root div (e.g. 'chapter-editor-page')
 *   children         page content
 */
function AppShell({ contextualItems = [], backTo = null, className = '', children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const globalItems = [
    { icon: BookOpen, label: 'My Books', onClick: () => navigate('/dashboard'), testId: 'nav-my-books' },
    { icon: Settings, label: 'Settings', onClick: () => navigate('/settings'), testId: 'nav-settings' },
  ];

  const close = () => setOpen(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div className={`story-page overflow-x-hidden ${className}`}>

      {/* Floating hamburger */}
      <button
        onClick={() => setOpen(true)}
        className="story-menu-button fixed top-3 left-3 z-40 flex h-9 w-9 items-center justify-center rounded-full transition-colors"
        aria-label="Open menu"
      >
        <Menu className="w-4 h-4" />
      </button>

      {/* Back button */}
      {backTo && (
        <button
          onClick={backTo.onClick}
          className="story-menu-button fixed top-3 left-14 z-40 flex h-9 items-center gap-1 rounded-full px-3 transition-colors"
          aria-label={`Back to ${backTo.label}`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{backTo.label}</span>
        </button>
      )}

      {/* Drawer backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={close}
      />

      {/* Drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 border-r border-[var(--story-line)] bg-[var(--story-bg)] transform transition-transform duration-200 overflow-y-auto ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4">

          {/* Drawer header */}
          <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#E7DED1]/70 dark:border-[#302820]/60">
            <span className="text-xs font-medium text-[#8C8176] dark:text-[#8F8174] uppercase tracking-widest">Menu</span>
            <button
              onClick={close}
              className="p-1.5 rounded-full hover:bg-[#F0E7DC] dark:hover:bg-[#2A2520]/50 text-[#5F554B] dark:text-[#C8BFB4]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Page-specific contextual items */}
          {contextualItems.length > 0 && (
            <>
              <div className="space-y-0.5 mb-1">
                {contextualItems.map((item, i) =>
                  item.divider
                    ? <div key={i} className="border-t border-[#E7DED1]/70 dark:border-[#302820]/60 my-3" />
                    : <DrawerItem key={item.label} item={item} onClose={close} />
                )}
              </div>
              {DIVIDER}
            </>
          )}

          {/* Global navigation */}
          <div className="space-y-0.5 mb-1">
            {globalItems.map(item => (
              <DrawerItem key={item.label} item={item} onClose={close} />
            ))}
          </div>

          {DIVIDER}

          {/* Dark mode */}
          <div className="px-3 py-2 flex items-center gap-3">
            <DarkModeToggle />
            <span className="text-sm text-[#2A2420] dark:text-[#F0EBE4]">Dark Mode</span>
          </div>

        </div>
      </div>

      {children}
    </div>
  );
}

export default AppShell;
